/**
 * Las guías, una sola vez para toda la app.
 *
 * Cinco pantallas necesitan el mismo conjunto de guías. Cada una lo pedía por
 * su cuenta y además se suscribía a los cambios para volver a pedirlo entero.
 * Contra Firestore eso se paga por documento leído: con 10.796 guías, abrir el
 * Resumen costaba más de 20.000 lecturas, y el plan gratuito da 50.000 al día.
 *
 * Aquí hay una sola suscripción viva. La primera entrega trae la colección;
 * después Firestore manda únicamente lo que cambió, y todas las pantallas se
 * enteran a la vez.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { suscribirGuias } from '@/db/repositorio';
import type { DocGuia } from '@/db/tiposDatos';
import { useAuth } from './useAuth';

interface Contexto {
  guias: DocGuia[];
  cargando: boolean;
}

const Ctx = createContext<Contexto>({ guias: [], cargando: true });

export function ProveedorGuias({ children }: { children: ReactNode }) {
  // Sin sesión no se lee nada: las reglas lo rechazarían.
  const { dentro, cargando: autenticando } = useAuth();

  const [guias, setGuias] = useState<DocGuia[]>([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    if (autenticando) return;
    if (!dentro) {
      setGuias([]);
      setCargando(false);
      return;
    }
    setCargando(true);
    return suscribirGuias((gs) => {
      setGuias(gs);
      setCargando(false);
    });
  }, [dentro, autenticando]);

  return <Ctx.Provider value={{ guias, cargando }}>{children}</Ctx.Provider>;
}

export function useGuias() {
  return useContext(Ctx);
}
